import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SpreadPhotoInfo.css';

// Explication d'une photo etalee sur DOUBLE PAGE, partagee par tous les endroits
// ou l'on voit le livre (atelier, previsualisation plein ecran, Aperçu final).
//
// Pourquoi : le rendu reprend une fine bande centrale de chaque cote du pli (rien
// n'est perdu a la reliure), ce qui se voit comme un leger doublon quand le livre
// est affiche a plat a l'ecran. Sans explication, ca ressemble a un defaut.
//
//  - icone (i) seule : ouvre/ferme une bulle (clic, Entree, Espace ; Echap ou clic
//    ailleurs referment) — pour les visionneuses ;
//  - `showAdvice` : en plus, la consigne courte reste affichee EN PERMANENCE (atelier) :
//    eviter de placer un visage pres de la reliure.
//
// `placement` : cote ou s'ouvre la bulle ('top' | 'bottom'), selon la place disponible ;
// `align` : 'center' (centree sur l'icone) ou 'start' (alignee a gauche, quand l'icone est au bord).
// `layflat` : reliure qui s'ouvre completement a plat (Prodigi layflat) — aucune perte au pli, l'image est CONTINUE : pas de doublon
// a expliquer, pas de consigne d'eviter le pli.
function SpreadPhotoInfo({ showAdvice = false, placement = 'top', align = 'center', tone = 'light', className = '', layflat = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const bubbleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`spread-info is-${tone} ${showAdvice ? 'has-advice' : ''} ${className}`}>
      <button
        type="button"
        className="spread-info-btn"
        aria-expanded={open}
        aria-controls={open ? bubbleId : undefined}
        aria-label={t('spreadInfo.label')}
        title={t('spreadInfo.label')}
        onClick={(event) => { event.stopPropagation(); setOpen((value) => !value); }}
      >
        <span aria-hidden="true">i</span>
      </button>
      {showAdvice && !layflat && <span className="spread-info-advice">{t('spreadInfo.short')}</span>}
      {open && (
        <div id={bubbleId} role="note" className={`spread-info-bubble is-${placement} is-align-${align}`}>
          <b>{t('spreadInfo.title')}</b>
          {layflat ? <p>{t('spreadInfo.layflat')}</p> : (<><p>{t('spreadInfo.continuity')}</p><p>{t('spreadInfo.advice')}</p></>)}
        </div>
      )}
    </div>
  );
}

export default SpreadPhotoInfo;
